import React from 'react';
import styled from 'styled-components';
import {
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  screenMdMin,
  display1FontSizeXSmall,
  heading2FontSize,
  spacer48,
  spacer40,
  screenSmMin,
  screenXsMax,
  Type,
  makeColorSetClass,
  cssColorValue,
} from '@spotify-internal/encore-web';
import { ModuleType } from '../types';
import {
  Box,
  Link,
  Supertext,
  StyledSupertext,
  withAnimationContext,
  DELAYS,
  fadeInAnimation,
} from '../../ui';
import { youtubeIdRegex } from '../../utils';
import { VideoComponent } from '../../ui/Video';
import { ShareButtonsComponent } from '../../ui/ShareButtons';
import { useT } from '../../../../features/src/i18n';

const Wrapper = styled(Box)`
  color: ${cssColorValue('textBase')};
  display: flex;
  flex-flow: row-reverse wrap;
  justify-content: space-between;
  height: auto;
  background: ${cssColorValue('backgroundBase')};
  & {
    padding: ${spacer48} ${spacer40};
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenMdMin}) {
    ${StyledSupertext} {
      padding-top: ${spacer32};
    }
  }

  @media (max-width: ${screenXsMax}) {
    padding-left: ${spacer16};
    padding-right: ${spacer16};
    p {
      padding-top: ${spacer16};
    }
  }

  @media (max-width: ${screenMdMin}) {
    flex-direction: column;
    justify-content: flex-start;
    padding-bottom: ${spacer32};
  }
`;

const InnerText = styled.div`
  padding-left: 0px;
  width: 30%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  @media (max-width: ${screenMdMin}) {
    justify-content: flex-start;
    width: 100%;
    margin: 0px;
  }
`;

const VideoWrapper = styled.div`
  padding-left: 0px;
  width: 65%;

  @media (max-width: ${screenMdMin}) {
    padding-bottom: ${spacer8};
    width: 100%;
    margin: 0px;
  }
`;

// TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific
const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding: ${spacer16} 0;
    font-size: ${display1FontSizeXSmall};
    max-width: 684px;

    @media (max-width: ${screenMdMin}) {
      text-align: left;
      font-size: ${heading2FontSize};
      padding: 0;
    }
  `,
);

const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-top: ${spacer16};
    padding-bottom: ${spacer24};
  `,
);

const AllVideosLink = withAnimationContext(
  styled(Link)`
    ${fadeInAnimation(DELAYS.FOURTH)};
    text-decoration: underline;
    color: ${cssColorValue('textBase')};

    &:focus:not(:disabled),
    &:active:not(:disabled),
    &:visited:not(:disabled) {
      color: ${cssColorValue('textBase')};
    }

    &:hover:not(:disabled) {
      color: ${cssColorValue('textSubdued')};
    }
  `,
);

export type VideoModuleType = ModuleType & {
  headline: string;
  subtext?: string;
  supertext?: string;
  videoUrl: string;
};

const TRACK_CAT = 'module-video';

export const VideoModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  VideoModuleType
>(({ headline = '', subtext = '', supertext = '', videoUrl = '' }, ref) => {
  const t = useT();
  return (
    <Wrapper
      ref={ref as React.RefObject<HTMLDivElement>}
      className={makeColorSetClass('invertedDark')}
    >
      <VideoWrapper>
        {videoUrl.match(youtubeIdRegex) && <VideoComponent url={videoUrl} />}
      </VideoWrapper>
      <InnerText>
        <div>
          {supertext && <Supertext>{supertext}</Supertext>}
          {headline && (
            <Headline data-testid="video-module-headline">{headline}</Headline>
          )}
          {subtext && <Subtext>{subtext}</Subtext>}
        </div>
        <div>
          <ShareButtonsComponent />
          <AllVideosLink href="/videos" track={[TRACK_CAT, 'All Videos Link']}>
            {t(
              'FRODOR_VIDEO_MODULE_BACK_TO_VIDEOS',
              'Back to videos',
              'A link that when clicked will take the user back to All Videos',
            )}
          </AllVideosLink>
        </div>
      </InnerText>
    </Wrapper>
  );
});
